import Button from '../common/Button';
import InlineStatus from '../common/InlineStatus';

interface RailChip {
  label: string;
  value: string;
}

interface RailStatusBlock {
  tone: 'neutral' | 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  meta?: string;
}

interface RailHistoryItem {
  id: string;
  provider: string;
  year: number;
  status: string;
  confirmationNumber: string | null;
  submittedAt: string | null;
}

interface RailAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  helper?: string;
  variant?: 'primary' | 'outline' | 'ghost';
}

interface SecondaryAction {
  key: string;
  label: string;
  onClick: () => void;
}

export interface TaxCompletionRailProps {
  headline: string;
  proofTime: string;
  chips: RailChip[];
  exportStatus: RailStatusBlock;
  filingStatus: RailStatusBlock;
  historyItems: RailHistoryItem[];
  historyLoading: boolean;
  historyEmptyMessage: string;
  dominantAction: RailAction;
  dominantActionId?: string;
  helperStatus?: RailStatusBlock | null;
  secondaryActions: SecondaryAction[];
  className?: string;
}

export default function TaxCompletionRail({
  headline,
  proofTime,
  chips,
  exportStatus,
  filingStatus,
  historyItems,
  historyLoading,
  historyEmptyMessage,
  dominantAction,
  dominantActionId,
  helperStatus = null,
  secondaryActions,
  className = '',
}: TaxCompletionRailProps) {
  return (
    <section className={`rounded-xl border border-blue-500/30 bg-blue-500/10 p-5 space-y-4 ${className}`.trim()}>
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-blue-300">
          Tax completion rail
        </p>
        <h2 className="text-xl font-semibold text-white">{headline}</h2>
      </div>

      <div className="rounded-lg border border-blue-400/20 bg-gray-950/40 px-4 py-3">
        <p className="text-[11px] uppercase tracking-wide text-blue-200/80">Exact proof time</p>
        <p className="mt-1 text-sm font-mono tabular-nums text-blue-100/90">{proofTime}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {chips.map((chip) => (
          <div key={`${chip.label}:${chip.value}`} className="rounded-full border border-gray-700 bg-gray-950/50 px-3 py-1.5 text-xs text-gray-300">
            {chip.label}:{' '}
            <span className="font-mono tabular-nums text-gray-100">{chip.value}</span>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-blue-400/20 bg-gray-950/40 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            id={dominantActionId}
            type="button"
            onClick={dominantAction.onClick}
            disabled={dominantAction.disabled}
            variant={dominantAction.variant ?? 'primary'}
            size="sm"
          >
            {dominantAction.label}
          </Button>
          {dominantAction.helper ? (
            <p className="text-xs text-blue-100/70 sm:mr-auto">{dominantAction.helper}</p>
          ) : null}
        </div>
      </div>

      <div className="space-y-3">
        <p className="text-[11px] uppercase tracking-wide text-white/40">Supporting proof</p>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(260px,320px)]">
          <div className="rounded-lg border border-white/10 bg-gray-950/40 p-4">
            <p className="mb-3 text-[11px] uppercase tracking-wide text-white/40">Export proof</p>
            <InlineStatus
              tone={exportStatus.tone}
              title={exportStatus.title}
              message={exportStatus.message}
              meta={exportStatus.meta}
              compact
            />
          </div>

          <div className="rounded-lg border border-white/10 bg-gray-950/40 p-4">
            <p className="mb-3 text-[11px] uppercase tracking-wide text-white/40">Filing proof</p>
            <InlineStatus
              tone={filingStatus.tone}
              title={filingStatus.title}
              message={filingStatus.message}
              meta={filingStatus.meta}
              compact
            />
          </div>

          <div className="rounded-lg border border-white/10 bg-gray-950/40 p-4 space-y-3">
            <p className="text-[11px] uppercase tracking-wide text-white/40">Recent history evidence</p>
            {historyLoading ? (
              <div className="space-y-2" aria-hidden="true">
                <div className="h-4 w-full animate-pulse rounded bg-white/5" />
                <div className="h-4 w-5/6 animate-pulse rounded bg-white/5" />
                <div className="h-4 w-2/3 animate-pulse rounded bg-white/5" />
              </div>
            ) : historyItems.length > 0 ? (
              <div className="space-y-2">
                {historyItems.map((item) => (
                  <div key={item.id} className="rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2 text-xs text-white/70">
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-medium uppercase text-white/80">{item.provider}</span>
                      <span className="font-mono tabular-nums text-white/50">{item.year}</span>
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-white/50">
                      <span className="font-medium text-white/70">{item.status}</span>
                      <span className="font-mono tabular-nums">{item.confirmationNumber ?? 'No confirmation yet'}</span>
                      <span className="font-mono tabular-nums">
                        {item.submittedAt ? new Date(item.submittedAt).toLocaleDateString() : 'Not submitted yet'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-white/50">{historyEmptyMessage}</p>
            )}
          </div>
        </div>
      </div>

      {helperStatus ? (
        <div className="rounded-lg border border-white/10 bg-gray-950/40 p-4">
          <InlineStatus
            tone={helperStatus.tone}
            title={helperStatus.title}
            message={helperStatus.message}
            meta={helperStatus.meta}
            compact
          />
        </div>
      ) : null}

      {secondaryActions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {secondaryActions.map((action) => (
            <button
              key={action.key}
              type="button"
              onClick={action.onClick}
              className="text-xs text-white/50 underline-offset-4 transition-colors hover:text-white/80 hover:underline"
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
